// Cross-boundary trigger for the in-app offline overlay.
//
// The overlay lives inside AuthedShell, but the things that open it don't all
// share its React tree: OfflineBanner is inside AuthedShell, but MobileNav is
// mounted at the root layout (outside it, so it can render on pre-auth routes).
// A window CustomEvent is the established way these two worlds talk to the shell
// (see `mobile-voice-tap`), so the overlay subscribes to one and every trigger
// dispatches it — no shared context needed.

export const OPEN_OFFLINE_OVERLAY_EVENT = 'mucka-offline-overlay-open';

/** Fire the "open the offline overlay" signal. Safe to call in SSR (no-op). */
export function openOfflineOverlay(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_OFFLINE_OVERLAY_EVENT));
}

/**
 * Subscribe to overlay-open requests. Returns an unsubscribe fn. SSR-safe.
 * Kept here (not inline in the component) so the wiring is unit-testable
 * without a DOM render.
 */
export function subscribeOpenOfflineOverlay(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(OPEN_OFFLINE_OVERLAY_EVENT, handler);
  return () => window.removeEventListener(OPEN_OFFLINE_OVERLAY_EVENT, handler);
}

/**
 * Decide what a mobile bottom-nav tab tap should do given connectivity.
 * Pure so it can be unit-tested without rendering MobileNav:
 *   online            → navigate as normal
 *   offline, same tab → no-op (re-navigating to where you are is pointless,
 *                       and we don't want to pop the overlay over the page
 *                       you're already reading)
 *   offline, new tab  → open the saved-work overlay instead of a dead nav
 */
export type NavAction = 'navigate' | 'open-overlay' | 'noop';

export function resolveNavAction(
  online: boolean,
  targetTab: string,
  activeTab: string | null,
): NavAction {
  if (online) return 'navigate';
  if (targetTab === activeTab) return 'noop';
  return 'open-overlay';
}
